import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';

import moment from 'moment-timezone';

import { CheckoutStatus } from '@shared/stripe/enums';
import { NotificationTime } from '@shared/restaurant/enums';
import { Notification, Party } from '@shared/party/models';
import { PartyTypes } from '@shared/party/enums';
import { FloorPlanParty, FloorPlanPartyType, FloorPlanPartyAction } from '@tablesready/frontend-timeline';
import { PartyBadge } from '@tablesready/frontend-badge';
import { FloorPlanTableService, MessageButtonService } from '../../../services';
import { NotifyActions } from '../../../components';
import { AppIcon, AppIconColor } from '@tablesready/frontend-icons';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';

@Component({
  selector: 'tablesready-table-info',
  templateUrl: './table-info.component.html',
  styleUrls: ['./table-info.component.scss'],
  providers: [MessageButtonService],
})
export class TableInfoComponent implements OnChanges, OnDestroy {
  @Output() deleteParty = new EventEmitter<unknown>();
  @Input() type = FloorPlanPartyType.booked;
  @Input() actions: FloorPlanPartyAction[] = [];
  @Input() notificationTime = NotificationTime.Relative;
  @Input() voicePageNotificationEnabled = false;
  @Input() selectedResourceId = '';
  @Input() currentFloorPlanId = '';
  @Input() showAlsoTables = false;
  @Input() timezone = '';
  @Input() party: FloorPlanParty;
  @Input() name = '';
  @Input() size = 0;
  @Input() createTime = 0;
  @Input() chipColor = 'red';
  @Input() chipText = '';
  @Input() country = '';
  @Input() iconColor: AppIconColor;
  @Input() partyBadge: PartyBadge;
  @Input() isReservation = false;
  @Input() isMobile = false;
  @Input() stripeData?: any;

  notifyActions?: NotifyActions<Party>;
  alsoTableNames?: string;
  iconColors = AppIconColor;
  icon = AppIcon;
  partyTypes = PartyTypes;
  floorPlanPartyTypes = FloorPlanPartyType;

  constructor(
    private readonly service: FloorPlanTableService,
    private readonly messagesButtonService: MessageButtonService,
    @Inject(APP_VARIABLES_TOKEN) private readonly appVariables: AppVariables,
  ) {
    this.notifyActions = {
      smsPage: (): void => {},
      disableMessageButton: (): boolean => true,
      mergeInboundOutbound: (party): Notification[] => {
        return this.messagesButtonService.mergeInboundOutbound(party);
      },
    };
  }

  onClick(pointerEvent: PointerEvent): void {
    pointerEvent.stopPropagation();
  }

  ngOnDestroy(): void {
    this.messagesButtonService.ngOnDestroy();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.showAlsoTables) {
      return;
    }

    if (changes.selectedResourceId || changes.party) {
      const otherResourceIds = this.party.resources.filter(res => res !== this.selectedResourceId);
      this.alsoTableNames = otherResourceIds.reduce((accumulator, currentResourceId, index, array) => {
        const nameWithPostfix = this.service.getResourceNameByIdWithFloorPlan(currentResourceId, this.currentFloorPlanId);

        return index < array.length - 1 ? `${accumulator}${nameWithPostfix}, ` : `${accumulator}${nameWithPostfix}`;
      }, '');
    }
  }

  get unpaid(): boolean {
    return this.isReservation && this.stripeData?.status === CheckoutStatus.inProgress
      && (this.createTime + this.appVariables.UNPAID_PARTY_LIFETIME * 1000) > Date.now();
  }

  get paid(): boolean {
    return this.isReservation && this.stripeData?.status === CheckoutStatus.paid;
  }

  get remainingSecondsToPay(): number {
    return this.createTime + (this.appVariables.UNPAID_PARTY_LIFETIME * 1000);
  }

  protected readonly PartyTypes = PartyTypes;
}
